class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    /* Locale already refused by /search/suggest — do not show a loading state we
       know will never resolve. The form still submits to the full search page. */
    if (this.predictiveUnsupported) {
      this.abortPredictive();
      return;
    }

    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    /* T20/B5: Dawn calls close() on a failed request but never clears the
       `loading` attribute or the live-region text, so the overlay is left
       spinning on "Loading…" forever. That is not hypothetical here — Shopify's
       /search/suggest rejects Arabic outright:

         GET /ar/search/suggest?q=لحم&…  ->  417 "Expectation failed: Unsupported buyer locale"

       (identical on the live theme, so it is a platform limit, not a regression —
       see work/t20/logs/ar-predictive.txt). Every keystroke in the AR overlay
       therefore parked the widget in a permanent loading state.

       The full results page is unaffected: /ar/search?q=… returns 200. So on
       failure, stop pretending to load and let the form submit normally. Once a
       locale has been refused, skip predictive entirely rather than firing a
       doomed request per keystroke. */
    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product&${encodeURIComponent('resources[limit]')}=4&section_id=predictive-search`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          if (response.status === 417 || response.status === 422) this.predictiveUnsupported = true;
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.abortPredictive();
        if (error && (error.message === '417' || error.message === '422')) return; // expected: locale not supported
        throw error;
      });
  }

  /* Leave no spinner behind: drop the loading flag, blank the live region and
     close the panel, so the search field behaves like a plain form. */
  abortPredictive() {
    this.setAttribute('loading', false);
    this.removeAttribute('loading');
    if (this.statusElement) {
      this.statusElement.textContent = '';
      this.statusElement.setAttribute('aria-hidden', 'true');
    }
    this.renderFallback();
  }

  /* T23/FE-01. T20 stopped the permanent spinner; this stops the silence that
     replaced it. On a locale Shopify's suggest endpoint refuses, an Arabic
     shopper typed and got an empty dropdown with no explanation, while
     /ar/search?q=… returns 200 and works perfectly. Offer that page.

     The label and the search route come from the header markup, so this file
     carries no strings and no hard-coded path. If either is missing — an older
     cached section, say — fall back to closing, which is what it did before. */
  renderFallback() {
    const term = this.getQuery();
    const label = this.getAttribute('data-fallback-label');
    const url = this.getAttribute('data-search-url');

    if (!term || !label || !url || !this.predictiveSearchResults) {
      this.close();
      return;
    }

    const href = url + '?q=' + encodeURIComponent(term) + '&options%5Bprefix%5D=last';
    this.predictiveSearchResults.innerHTML =
      '<div class="predictive-search predictive-search--header" tabindex="-1">' +
        '<ul class="predictive-search__results-list" role="listbox">' +
          '<li class="predictive-search__list-item" role="option" aria-selected="false">' +
            '<a class="predictive-search__item predictive-search__item--term" href="' + href + '"></a>' +
          '</li>' +
        '</ul>' +
      '</div>';
    /* textContent, not string concatenation: the term is whatever was typed. */
    this.predictiveSearchResults.querySelector('a').textContent = label + ' \u201C' + term + '\u201D';

    this.setAttribute('results', true);
    this.open();
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.getElementById('shopify-section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');

    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);
